import React, { useEffect, useState, useRef } from 'react';
import BBALoader from './BBALoader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const UserIntegrationCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing integration...');
  const isProcessingRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      console.log('UserIntegrationCallback: Starting callback processing', {
        currentUser: !!currentUser,
        isProcessing: isProcessingRef.current,
        search: window.location.search,
        pathname: window.location.pathname
      });
      
      // Prevent multiple simultaneous processing
      if (isProcessingRef.current) return;
      
      isProcessingRef.current = true;
      
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          setStatus('error');
          setMessage(`Integration failed: ${error}`);
          setTimeout(() => {
            navigate('/crm/profile');
          }, 3000);
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setMessage('Missing required parameters');
          setTimeout(() => {
            navigate('/crm/profile');
          }, 3000);
          return;
        }

        // Parse state parameter: "integrationKey:userId"
        let integrationKey, userId;
        
        if (state.includes(':')) {
          [integrationKey, userId] = state.split(':');
          
          // Verify the user ID matches the current user
          if (currentUser && userId !== currentUser.uid) {
            setStatus('error');
            setMessage('User ID mismatch');
            setTimeout(() => {
              navigate('/crm/profile');
            }, 3000);
            return;
          }
        } else {
          setStatus('error');
          setMessage('Invalid state parameter');
          setTimeout(() => {
            navigate('/crm/profile');
          }, 3000);
          return;
        }
        
        if (!integrationKey || !userId) {
          setStatus('error');
          setMessage('Invalid state parameter or user not authenticated');
          setTimeout(() => {
            navigate('/crm/profile');
          }, 3000);
          return;
        }

        setMessage(`Connecting ${integrationKey}...`);

        const redirectUriByKey = {
          fathom: `${window.location.origin}/auth/fathom/callback`,
          googleCalendar: `${window.location.origin}/auth/google/callback`,
          gmail: `${window.location.origin}/auth/google/callback`,
          calendly: `${window.location.origin}/auth/calendly/callback`,
          slack: `${window.location.origin}/auth/slack/callback`,
        };
        const userRedirectUri =
          redirectUriByKey[integrationKey] || `${window.location.origin}/auth/google/callback`;

        // Call the Firebase function to handle the OAuth callback
        const { handleUserIntegrationCallback } = await import('../services/userIntegrationService');
        
        try {
          let codeVerifier = null;
          let oauthClientId = null;
          if (integrationKey === 'calendly') {
            const pkceKey = `oauth_pkce_calendly_${userId}`;
            const clientIdKey = `oauth_calendly_client_id_${userId}`;
            codeVerifier = sessionStorage.getItem(pkceKey);
            oauthClientId = sessionStorage.getItem(clientIdKey);
            if (!codeVerifier) {
              setStatus('error');
              setMessage('Calendly connection expired or missing PKCE verifier. Please try Connect again.');
              setTimeout(() => {
                navigate('/crm/profile', { replace: true });
              }, 2000);
              return;
            }
            sessionStorage.removeItem(pkceKey);
            sessionStorage.removeItem(clientIdKey);
          }

          const result = await handleUserIntegrationCallback(
            integrationKey,
            code,
            userId,
            userRedirectUri,
            codeVerifier,
            oauthClientId
          );
          
          if (result.success) {
            setStatus('success');
            setMessage(`${integrationKey} connected successfully!`);
            // Redirect immediately to prevent auth timeout
            setTimeout(() => {
              navigate('/crm/profile', { replace: true });
            }, 1000);
          } else {
            setStatus('error');
            setMessage(`Failed to connect ${integrationKey}: ${result.error}`);
            setTimeout(() => {
              navigate('/crm/profile', { replace: true });
            }, 2000);
          }
        } catch (error) {
          console.error('Error calling handleUserIntegrationCallback:', error);
          setStatus('error');
          setMessage(`Error connecting ${integrationKey}: ${error.message}`);
          setTimeout(() => {
            navigate('/crm/profile', { replace: true });
          }, 2000);
        } finally {
          isProcessingRef.current = false;
        }

      } catch (error) {
        console.error('Error handling integration callback:', error);
        setStatus('error');
        setMessage(`Error: ${error.message}`);
        setTimeout(() => {
          navigate('/crm/profile');
        }, 3000);
        isProcessingRef.current = false;
      }
    };

    handleCallback();
  }, [searchParams, navigate, currentUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          {status === 'processing' && (
            <BBALoader size={48} className="mx-auto mb-4" />
          )}
          
          {status === 'success' && (
            <div className="text-green-600 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          
          {status === 'error' && (
            <div className="text-red-600 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}

          <h2 className={`text-xl font-semibold mb-2 ${
            status === 'success' ? 'text-green-600' : 
            status === 'error' ? 'text-red-600' : 'text-gray-900'
          }`}>
            {status === 'success' ? 'Success!' : 
             status === 'error' ? 'Error' : 'Processing'}
          </h2>
          
          <p className="text-gray-600 mb-4">{message}</p>
          
          <button
            onClick={() => navigate('/crm/profile', { replace: true })}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Return to Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserIntegrationCallback;
